<template>
  <div class="editable-text" :class="{ editing: isEditing, 'not-editing': !isEditing }" v-click-away="finishEditing">
    <div id="text-editor-box" class="write-view" @click="startEditing">
      <div
        :id="editableTextId"
        class="text-editor"
        ref="editableDiv"
        contenteditable="true"
        @mousedown="saveSelection"
        @mouseup="saveSelection"
        @keyup="saveSelection"
        @keydown.enter.exact.prevent
        @keyup.enter.exact="finishEditing"
        :data-placeholder="getPlaceholder()"
      ></div>
    </div>
    <div @click="startEditing">
      <transition name="expand">
        <ActionBar
          class="action-bar"
          v-show="isEditing"
          :show-variable-picker="showVariablePicker"
          @addVariable="addVariable"
          @addEmoji="addEmoji"
        />
      </transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { InputVariableViewModel } from '@/features/bots/models/designer';
import ActionBar from '@/features/bots/components/designer/TextEditor/ActionBar.vue';
import VariableBadge from './VariableBadge.vue';
import Toast from '@/shared/services/toast';
import { nanoid } from 'nanoid';
import HtmlFormatter from '@/shared/services/htmlFormatter';
import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import { ContactAttributeType } from '@/messaging/models/contactAttributeType';
import { useStore } from 'vuex';
import { computed, onBeforeMount, onMounted, ref, watch, createApp } from 'vue';
import { ComponentPublicInstance } from '@vue/runtime-core';
import { createAppWithPlugins } from '@/utils/createAppWithPlugins';

const props = defineProps({
  value: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  showVariablePicker: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits<{
  (e: 'error', invalid: boolean): void;
  (e: 'text-updated', text: string): void;
}>();

defineExpose({
  focusElement,
  startEditing,
});

const store = useStore();
const getAttribute = (attributeId: string) => computed(() => store.getters.getAttribute(attributeId));
const contactAttributes = computed<ContactAttributeViewModel[]>(() => store.getters.contactAttributes);

const variableIds = ref<string[]>([]);
const selection = ref<any>(null);
const text = ref('');
const isEditing = ref(false);
const editableTextId = ref('');
const idAndVariable = ref(new Map<string, InputVariableViewModel>());
const editableDiv = ref<HTMLDivElement | null>(null);
const badgeInstances = ref<{ [key: string]: ComponentPublicInstance<InstanceType<typeof VariableBadge>> }>({});
const containsInvalidAttribute = ref(false);

watch(contactAttributes, () => reevaluateBadgeComponent(), { immediate: true });
onBeforeMount(() => (editableTextId.value = nanoid(7)));

onMounted(() => {
  setupEditableDivEvents();
  let newValue = props.value;
  editableDiv.value?.focus();
  restoreSelection();
  newValue = HtmlFormatter.escapeHtmlCharacters(newValue);
  newValue = HtmlFormatter.convertNewLinesToBreaks(newValue);

  if (props.showVariablePicker && containsVariable()) {
    newValue = convertVariablesToHtml(newValue);
    HtmlFormatter.pasteHtmlAtCaret(newValue);
    for (const id of variableIds.value) {
      insertVariableBadgeElement(id);
    }
  } else {
    HtmlFormatter.pasteHtmlAtCaret(newValue);
  }

  HtmlFormatter.setCaretAtTheEndOfContentEditable(editableDiv.value!);

  // blur the element if it already has a value
  if (props.value.length && props.value.length > 0) {
    blurElement();
  }
  emit('error', containsInvalidAttribute.value);
});

function hasText() {
  return text.value && text.value.length > 0;
}

function startEditing() {
  isEditing.value = true;
  focusElement();
}

function finishEditing(event: any) {
  if (!isEditing.value) {
    return;
  }

  if (hasClickedTooltip(event)) {
    return;
  }
  isEditing.value = false;
  editableDiv.value?.blur();

  convertToProcessableText();
  emit('error', containsInvalidAttribute.value);
}

function addVariable(variable: InputVariableViewModel) {
  editableDiv.value?.focus();
  restoreSelection();
  const id = `pill-${nanoid(7)}`;
  variableIds.value.push(id);
  const inputAsHtml = `<div id="${id}" style="display: inline-block;"></div>`;

  HtmlFormatter.pasteHtmlAtCaret(inputAsHtml);

  createVariableBadgeComponent(id, variable);
  HtmlFormatter.setCaretAfterElement(id);
}

function addEmoji(emoji: any) {
  editableDiv.value?.focus();
  restoreSelection();
  HtmlFormatter.pasteHtmlAtCaret(emoji);
}

function getPlaceholder() {
  return props.placeholder ? props.placeholder : '&nbsp;';
}

function saveSelection() {
  isEditing.value = true;
  selection.value = HtmlFormatter.getSelection();
}

function focusElement() {
  editableDiv.value?.focus();
  if (editableDiv.value?.innerText?.length === 0) {
    HtmlFormatter.setCaretAtTheEndOfContentEditable(editableDiv.value);
  }
}

function blurElement() {
  editableDiv.value?.blur();
}

function insertVariableBadgeElement(id: string) {
  const variable = idAndVariable.value.get(id);
  if (variable) {
    createVariableBadgeComponent(id, variable);
  }
  HtmlFormatter.setCaretAtTheEndOfContentEditable(editableDiv.value!);
}

function containsVariable() {
  return props.value.includes('{{');
}

function hasClickedTooltip(event: any) {
  return (
    event &&
    event.toElement &&
    event.toElement.className &&
    typeof event.toElement.className === 'string' &&
    (event.toElement.className.startsWith('tooltip') || event.toElement.className.startsWith('fallback-text'))
  );
}

function setupEditableDivEvents() {
  if (editableDiv.value) {
    editableDiv.value.addEventListener('paste', (e: ClipboardEvent) => {
      e.preventDefault();
      if (e && e.clipboardData) {
        let text = e.clipboardData.getData('text/plain');
        text = HtmlFormatter.escapeHtmlCharacters(text);
        text = HtmlFormatter.convertNewLinesToBreaks(text);
        document.execCommand('insertHTML', false, text);
      }
    });

    editableDiv.value.addEventListener('keydown', (e: KeyboardEvent) => {
      const bannedCommands = 'iIbBuU';
      if (e.ctrlKey && bannedCommands.includes(e.key)) {
        e.preventDefault();
      }
    });
  }
}

function restoreSelection() {
  if (selection.value) {
    HtmlFormatter.setSelection(selection.value);
  }
}

function convertVariablesToHtml(newValue: string) {
  const regexForVariable = /{{.*?.}}/;
  while (regexForVariable.exec(newValue)) {
    const variableRegArray = regexForVariable.exec(newValue);
    if (variableRegArray && variableRegArray[0]) {
      newValue = convertVariableToHtml(variableRegArray, newValue);
    }
  }
  return newValue;
}

function createVariableBadgeComponent(id: string, variable: InputVariableViewModel) {
  const app = createAppWithPlugins(VariableBadge, {
    id,
    variable,
    fallback: variable.fallback || '',
    onClosedFallback: finishEditing,
  });

  const container = document.getElementById(id);
  if (container) {
    const instance = app.mount(container) as ComponentPublicInstance<InstanceType<typeof VariableBadge>>;
    variable.id = id;
    badgeInstances.value[id] = instance;
  }
}

function convertToProcessableText() {
  if (editableDiv.value) {
    let html = editableDiv.value.innerHTML;
    html = convertHtmlElementsToVariables(html);
    html = HtmlFormatter.cleanseHtml(html);
    html = HtmlFormatter.convertEscapedHtmlToString(html);
    emit('text-updated', html);
  }
}

function convertHtmlElementsToVariables(html: string) {
  containsInvalidAttribute.value = false;
  for (const id of variableIds.value) {
    const currentVariable = document.getElementById(id) as HTMLElement;
    if (currentVariable) {
      html = convertHtmlElementToVariable(currentVariable, html);
    }
  }
  return html;
}

function convertHtmlElementToVariable(currentVariable: HTMLElement, html: string): string {
  const pillElements = currentVariable.getElementsByClassName('pill');
  if (pillsContainInvalidAttribute(pillElements)) {
    containsInvalidAttribute.value = true;
  }
  if (pillElements.length > 0 && pillElements[0]) {
    const elementWithData = pillElements[0];
    const fallback = elementWithData.getAttribute('data-fallback');
    const scope = elementWithData.getAttribute('data-scope');
    const key = elementWithData.getAttribute('data-key');
    const elementAsHtml = currentVariable.outerHTML;
    let scopePrefix = '';
    if (scope === 'appUser' || scope === 'appUser.customAttributes') {
      scopePrefix = `${scope}.`;
    }
    html = replaceHtmlWithVariable(fallback, html, elementAsHtml, scopePrefix, key);
  }
  return html;
}

function pillsContainInvalidAttribute(pillElements: HTMLCollectionOf<Element>) {
  for (const pillElement of pillElements) {
    if (pillIsInvalid(pillElement)) {
      return true;
    }
  }
  return false;
}

function replaceHtmlWithVariable(
  fallback: string | null,
  html: string,
  elementAsHtml: string,
  scopePrefix: string,
  key: string | null,
): string {
  if (!key) {
    Toast.showError('There was an error');
  }
  if (fallback) {
    html = html.replace(elementAsHtml, `{{${scopePrefix}${key}|'${fallback}'}}`);
  } else {
    html = html.replace(elementAsHtml, `{{${scopePrefix}${key}|''}}`);
  }
  return html;
}

function convertVariableToHtml(variableRegArray: any[], newValue: string) {
  const regexForVariable = /{{.*?.}}/;
  const variableWithBracket = variableRegArray[0];
  const variableWithoutBracket = variableWithBracket.substring(2, variableWithBracket.length - 2);

  const variableData = variableWithoutBracket.split('|');
  const variableSyntax = variableData[0];
  const fallback = variableData[1];

  const variable = extractVariableAndScope(variableSyntax);

  variable.fallback = fallback.substring(1, fallback.length - 1);
  const id = `pill-${nanoid(7)}`;
  variableIds.value.push(id);

  idAndVariable.value.set(id, variable);

  const inputAsHtml = `<div id="${id}"></div>`;

  newValue = newValue.replace(regexForVariable, inputAsHtml);

  editableDiv.value?.focus();
  return newValue;
}

function extractVariableAndScope(variableSyntax: string): InputVariableViewModel {
  const syntaxParts = variableSyntax.split('.');

  if (syntaxParts[2]) {
    return convertCustomAttributeVariable(syntaxParts);
  } else if (syntaxParts[1]) {
    return convertSystemVariable(syntaxParts);
  } else {
    return {
      id: '',
      key: syntaxParts[0].trim(),
      name: syntaxParts[0].trim(),
      scope: 'flow',
    } as InputVariableViewModel;
  }
}

function convertCustomAttributeVariable(value: string[]): InputVariableViewModel {
  let dataType = ContactAttributeType.Text;
  const variableScope = `${value[0]}.${value[1]}`;
  const variableKey = value[2].trim();
  let variableName = variableKey;
  let error = '';
  const contactAttribute = getAttribute(variableKey).value;
  if (contactAttribute) {
    dataType = contactAttribute.type;
    variableName = contactAttribute.name;

    if (contactAttribute.archived) {
      error = 'Attribute has been archived';
      containsInvalidAttribute.value = true;
    }
  } else {
    error = "Attribute doesn't exist";
    containsInvalidAttribute.value = true;
  }
  return {
    id: '',
    key: variableKey,
    name: variableName,
    scope: variableScope,
    dataType,
    error,
  } as InputVariableViewModel;
}

function convertSystemVariable(value: string[]): InputVariableViewModel {
  const variableScope = value[0].trim();
  const variableKey = value[1].trim();
  let variableName = variableKey;
  let dataType = ContactAttributeType.Text;
  const contactAttribute = getAttribute(variableKey).value;
  if (contactAttribute) {
    variableName = contactAttribute.name;
    dataType = contactAttribute.type;
  }
  if (variableKey === 'surname') {
    variableName = 'Last Name';
  } else if (variableKey === 'givenName') {
    variableName = 'First Name';
  }
  return {
    id: '',
    key: variableKey,
    name: variableName,
    scope: variableScope,
  } as InputVariableViewModel;
}

function reevaluateBadgeComponent() {
  idAndVariable.value.forEach((variable) => {
    if (variable.error) {
      reevaluateInvalidBadgeComponent(variable);
    }
  });
}

function reevaluateInvalidBadgeComponent(variable: InputVariableViewModel) {
  const contactAttribute = getAttribute(variable.key).value;
  if (contactAttribute) {
    // Previously missing attribute now exists, clear error
    variable.error = '';
    variable.dataType = contactAttribute.type;
    variable.name = contactAttribute.name;

    const badge = badgeInstances.value[variable.id] as unknown as InstanceType<typeof VariableBadge>;
    badge.update();
  }
}

function pillIsInvalid(pillElement: Element) {
  const key = pillElement.getAttribute('data-key');
  const scope = pillElement.getAttribute('data-scope');
  if (key && scope === 'appUser.customAttributes') {
    const attribute = getAttribute(key).value;
    if ((attribute && attribute.archived) || !attribute) {
      return true;
    }
  }
  return false;
}
</script>

<style scoped lang="scss">
/*So it doesnt change the positioning of the text box when toggling between editing/notediting*/
.not-editing {
  border: 1px solid transparent;
  box-shadow:
    inset 0 1px 2px transparent,
    0 0 5px transparent;
  border-radius: 4px;
}

.editing {
  border: 1px solid #3273dc;
  box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
  border-radius: 4px;
}

.placeholder {
  opacity: 0.6;
}

.write-view {
  line-height: inherit;
  padding: 0px 5px 0px 0px;

  input[type='text'],
  div {
    width: 100%;
    padding: 0;
    border: none;
    background-color: transparent;
    font-size: inherit;
    color: inherit;
  }

  input[type='text']:focus,
  div:focus {
    outline-style: none;
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: height 0.25s;

  &.action-bar {
    visibility: hidden;
  }
}

.expand-enter,
.expand-leave-to {
  height: 0;
}

.expand-enter-to,
.expand-leave {
  height: 26px;
}

.text-editor {
  border: none;
  word-break: break-word;
}

[contentEditable='true']:empty:not(:focus):before {
  content: attr(data-placeholder);
  color: #7d7d7d;
}

.editable-text {
  padding-left: 4px;
}
</style>
